import { createSelector } from "reselect";
import { selectArtistMap } from "../common/commonSelector";
import { decodeToken } from "../utils/jwt";

/**
 * Checks if a value is a valid JWT token string.
 * Guards against non-string values (e.g., {}, [], null) that would crash decodeToken.
 */
const isValidTokenString = (token) => typeof token === 'string' && token.length > 0;

export const selectAuthState = createSelector(
    [(state) => state]
    , (state) => state.auth
)

export const selectStudioAuth = createSelector([selectAuthState], state => state?.studioAuth)


export const selectStudioToken = createSelector([selectStudioAuth], state => state?.studioToken)
export const selectStudioRefreshToken = createSelector([selectStudioAuth], state => state?.studioRefreshToken)
export const selectIsStudioLoginFailed = createSelector([selectAuthState], (state) => Boolean(state.studioAuthFailure))
export const selectIsStudioLoginInProgress= createSelector([selectAuthState], (state) => Boolean(state.studioAuthLoading))

export const selectArtistAuth = createSelector([selectAuthState], (state) => state?.artistAuth)

export const selectArtistToken = createSelector([selectArtistAuth], state => state?.artistToken)
export const selectIsArtistLoggedIn = createSelector([selectArtistAuth], state => Boolean(state?.artistToken))
export const selectArtistRefreshToken = createSelector([selectArtistAuth], state => state?.artistRefreshToken)
export const selectLoggedInArtistID = createSelector(
    [selectArtistRefreshToken]
    , token => isValidTokenString(token) ? decodeToken(token).id : undefined
)

export const selectDecodedToken = createSelector(
    [selectArtistToken]
    , token => isValidTokenString(token) ? decodeToken(token) : undefined
)
export const selectLoggedInArtistData = createSelector([selectDecodedToken], (token) => token?.artist)
export const selectLoggedInArtistKuerzel = createSelector(
    [selectLoggedInArtistID, selectArtistMap],
    (artistId, artistMap) => artistId && artistMap?.[artistId]?.Kuerzel
)

export const selectLoggedInIsAdmin = createSelector(
    [selectLoggedInArtistID, selectArtistMap],
    (artistId, artistMap) =>
        Boolean(artistId && artistMap?.[artistId]?.isAdmin)
)
export const selectLoggedInCannotEdit = createSelector(
    [selectLoggedInArtistID, selectArtistMap],
    (artistId, artistMap) =>
        Boolean(artistId && artistMap?.[artistId]?.CannotEdit)
)


export const selectLoggedInStudioData = createSelector(
    [selectArtistToken]
    , (token) => isValidTokenString(token) ? decodeToken(token).studio : undefined
)

export const selectStudioDBName = createSelector([selectLoggedInStudioData], studio => studio?.db)
export const selectStudioUserID = createSelector([selectLoggedInStudioData], studio => studio?.UserID)
export const selectHasKissSign = createSelector([selectLoggedInStudioData], studio => studio?.SignPadCount > 0)

// Returns the logged-in artist with isAdmin/CannotEdit flags.
export const selectLoggedInArtist = createSelector(
    [selectLoggedInArtistID, selectArtistMap],
    (artistId, artistMap) => {
        const artist = artistId && artistMap?.[artistId]
        if (!artist) return undefined
        const combinedIsAdmin = Boolean(artist.isAdmin)
        const combinedCannotEdit = Boolean(artist.CannotEdit)
        if (artist.isAdmin === combinedIsAdmin && Boolean(artist.CannotEdit) === combinedCannotEdit) {
            return artist
        }
        return { ...artist, isAdmin: combinedIsAdmin, CannotEdit: combinedCannotEdit }
    }
)
export const selectIsArtistLoginFailed = createSelector([selectAuthState], state => Boolean(state?.artistAuthFailure))
export const selectIsArtistLoginInProgress= createSelector([selectAuthState], state => Boolean(state?.artistAuthLoading))
export const selectStudioLoginResetResult = createSelector([selectAuthState], state => state?.studioLoginResetResult)
export const selectStudioLoginResetResultError = createSelector([selectStudioLoginResetResult], state => state?.error)
export const selectArtistLoginResetResult = createSelector([selectAuthState], state => state?.artistLoginResetResult)
export const selectArtistLoginResetResultError = createSelector([selectArtistLoginResetResult], state => state?.error)


